import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Toaster } from 'sonner';
import { AppLayout } from './layouts/AppLayout';
// Remove this import since we don't need it here anymore
// import { DashboardLayout } from './layouts/DashboardLayout';
import { LandingPage, DashboardPage, AuthPage, LoginPage, ForgotPasswordPage, ResetPasswordPage, TermsPage, PrivacyPage, MaintenancePage } from './pages';
import { PublicRoute, ProtectedRoute } from './components/auth';
import { Box, CircularProgress } from '@mui/material';
import { apiClientBff } from './services/apiBff';
import MilkProductionEconomicsView from './modules/dairy/components/MilkProductionEconomicsView';
import SubscriptionRequiredPage from './pages/SubscriptionRequiredPage';
import SubscriptionDetailsPage from './pages/SubscriptionDetailsPage';
// Removed standalone profile page; use dashboard with profile view
import CompleteProfilePage from './pages/CompleteProfilePage';
import BrandShowcasePage from './pages/BrandShowcasePage';
import WeaningFeedbackExample from './pages/WeaningFeedbackExample';


import PlanSelectionScreen from './modules/onboarding/components/PlanSelectionScreen';
import AsaasPaymentScreen from './modules/onboarding/components/AsaasPaymentScreen';
import TestIntegration from './pages/TestIntegration';
import { AuthProvider } from './contexts/AuthContext';
import WelcomeToSubscriptionPage from './pages/WelcomeToSubscriptionPage';
import CongratulationsPage from './pages/CongratulationsPage';
import CancelSubscriptionPage from './pages/CancelSubscriptionPage';

import { FinishRegistrationPage } from './pages/FinishRegistrationPage';
import OnboardingWebViewSignupPage from './modules/OnboardingWebView/pages/OnboardingWebViewSignupPage';
import OnboardingWebViewPaymentPage from './modules/OnboardingWebView/pages/OnboardingWebViewPaymentPage';

import { Loading } from './components/ui/Loading';

const ErrorFallback: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      p: 4,
      textAlign: 'center',
    }}
  >
    <h2>Algo deu errado</h2>
    <p>Ocorreu um erro inesperado. Por favor, tente novamente.</p>
    <button
      onClick={() => window.location.reload()}
      style={{ padding: '8px 16px', cursor: 'pointer', marginTop: 8 }}
    >
      Tentar novamente
    </button>
  </Box>
);

const App: React.FC = () => {
  const [isMaintenance, setIsMaintenance] = React.useState(false);
  const [checkingMaintenance, setCheckingMaintenance] = React.useState(true);

  React.useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await apiClientBff.get<{ maintenance: boolean }>('/status');
        setIsMaintenance(response.maintenance);
      } catch (error) {
        console.error('Failed to check maintenance status', error);
        setIsMaintenance(false);
      } finally {
        setCheckingMaintenance(false);
      }
    };
    checkMaintenance();
  }, []);

  if (checkingMaintenance) {
    return (
      <Loading message="Iniciando aplicação..." fullScreen={true} />
    );
  }

  if (isMaintenance) {
    return (
      <AppLayout>
        <MaintenancePage />
      </AppLayout>
    );
  }

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <AuthProvider>
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: '#004e64',
              color: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              fontFamily: '"Inter", sans-serif',
              fontSize: '0.875rem',
            },
          }}
          theme="dark"
        />
        <AppLayout>
          <Router future={{ v7_startTransition: true }}>
            <Routes>
              {/* Landing page with marketing header guarded as public */}
              <Route 
                path="/" 
                element={
                  <PublicRoute>
                    <LandingPage />
                  </PublicRoute>
                } 
              />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/auth/finish-registration" element={<FinishRegistrationPage />} />
              <Route path="/onboarding-webview" element={<Navigate to="/onboarding-webview/signup" replace />} />
              <Route path="/onboarding-webview/signup" element={<OnboardingWebViewSignupPage />} />
              <Route path="/onboarding-webview/plan-selection" element={<PlanSelectionScreen />} />
              <Route path="/onboarding-webview/payment" element={<OnboardingWebViewPaymentPage />} />
              <Route 
                path="/auth/forgot-password" 
                element={
                  <PublicRoute>
                    <ForgotPasswordPage />
                  </PublicRoute>
                } 
              />
              <Route 
                path="/auth/reset-password" 
                element={
                  <PublicRoute>
                    <ResetPasswordPage />
                  </PublicRoute>
                } 
              />
              {/* Alias /login to the same auth page */}
              <Route path="/login" element={<LoginPage />} />
              
              {/* Plan selection screen for paid plans */}
              <Route path="/plan-selection" element={<PlanSelectionScreen />} />
              
              {/* Removed: Plans page route; details moved to Profile */}
              
              {/* Payment screen (Asaas) */}
              <Route path="/payment" element={<AsaasPaymentScreen />} />
              
              {/* Stepped payment flow (Stripe) removido */}
              
              {/* Welcome to subscription page */}
              <Route path="/welcome-subscription" element={<WelcomeToSubscriptionPage />} />
              {/* Cancel subscription confirmation page */}
              <Route path="/cancel-subscription" element={<CancelSubscriptionPage />} />
              
              {/* Congratulations page */}
              <Route path="/congratulations" element={<CongratulationsPage />} />

              {/* Subscription required page */}
              <Route path="/subscription-required" element={<SubscriptionRequiredPage />} />

              {/* Subscription details page */}
              <Route path="/subscription" element={<SubscriptionDetailsPage />} />
              
              {/* Test integration page */}
              <Route path="/test-integration" element={<TestIntegration />} />

              
              {/* Dashboard with app header */}
              <Route 
                path="/dashboard" 
                element={<DashboardPage />} 
              />
              
              {/* Profile route now redirects to dashboard with profile view */}
              <Route 
                path="/profile" 
                element={<Navigate to="/dashboard" state={{ contentView: 'profile' }} replace />} 
              />
              <Route 
                path="/profile/complete" 
                element={<CompleteProfilePage />} 
              />
              
              {/* Brand Identity Showcase (for internal review) */}
              <Route 
                path="/brand-showcase" 
                element={<BrandShowcasePage />} 
              />
              
              {/* Weaning Feedback Example (for testing) */}
              <Route 
                path="/weaning-feedback-example" 
                element={<WeaningFeedbackExample />} 
              />
              
              {/* Milk Production Economics Mockup */}
              <Route 
                path="/milk-economics-mockup" 
                element={
                  <ProtectedRoute>
                    <Box sx={{ p: 4, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
                       <MilkProductionEconomicsView />
                    </Box>
                  </ProtectedRoute>
                } 
              />
              


            </Routes>
          </Router>
        </AppLayout>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;
